
import { startOfWeek, endOfWeek, format, addDays, endOfDay } from 'date-fns';

// Calculate date boundaries for a given week
export const calculateWeekBoundaries = (baseDate: Date) => {
  const weekStartDate = startOfWeek(baseDate, { weekStartsOn: 1 }); // Monday
  const weekEndDate = endOfDay(addDays(weekStartDate, 6)); // End of Sunday
  const formattedWeekStart = format(weekStartDate, 'yyyy-MM-dd');
  const weekStartFormatted = format(weekStartDate, 'MMM dd');
  const weekEndFormatted = format(weekEndDate, 'MMM dd, yyyy');
  
  return {
    weekStartDate,
    weekEndDate,
    formattedWeekStart,
    weekStartFormatted,
    weekEndFormatted
  };
};

// Format date range for logging
export const formatDateRangeForLog = (start: Date, end: Date) => {
  return `${format(start, 'yyyy-MM-dd')} to ${format(end, 'yyyy-MM-dd')}`;
};

// Helper function to smoothly transition between weeks
export const getTransitionStyle = (isTransitioning: boolean) => {
  return {
    transition: `opacity ${isTransitioning ? '0.2s' : '0.3s'} ease-in-out, 
                 transform ${isTransitioning ? '0.2s' : '0.3s'} ease-in-out`,
    opacity: isTransitioning ? 0 : 1,
    transform: isTransitioning ? 'translateY(5px)' : 'translateY(0)',
    willChange: 'opacity, transform',
    pointerEvents: isTransitioning ? 'none' : 'auto'
  };
};

// Calculate shift duration returning total minutes for exact calculation
export const calculateShiftDurationInMinutes = (startDate: Date, endDate: Date): number => {
  // If the shift ends exactly at midnight (next day), calculate minutes till midnight
  if (endDate.getHours() === 0 && 
      endDate.getMinutes() === 0 && 
      endDate.getDate() !== startDate.getDate()) {
    // Calculate minutes from start time until midnight (24:00)
    const startHours = startDate.getHours();
    const startMinutes = startDate.getMinutes();
    const minutesUntilMidnight = (24 * 60) - (startHours * 60 + startMinutes);
    return minutesUntilMidnight;
  }
  // Regular calculation for other shifts
  const durationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  return Math.round(durationMinutes); // Round to nearest minute
};

// Calculate shift duration in hours (for backward compatibility)
export const calculateShiftDuration = (startDate: Date, endDate: Date): number => {
  return calculateShiftDurationInMinutes(startDate, endDate) / 60;
};

// Convert minutes to hours and minutes object
export const minutesToHoursAndMinutes = (totalMinutes: number): { hours: number; minutes: number } => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
};

// Format hours and minutes to Greek text
export const formatHoursAndMinutes = (totalMinutes: number): string => {
  const { hours, minutes } = minutesToHoursAndMinutes(totalMinutes);
  
  if (hours === 0 && minutes === 0) {
    return "0 λεπτά";
  } else if (hours === 0) {
    return `${minutes} λεπτ${minutes === 1 ? 'ό' : 'ά'}`;
  } else if (minutes === 0) {
    return `${hours} ώρ${hours === 1 ? 'α' : 'ες'}`;
  } else {
    return `${hours} ώρ${hours === 1 ? 'α' : 'ες'} & ${minutes} λεπτ${minutes === 1 ? 'ό' : 'ά'}`;
  }
};

// Round up a number of hours to the nearest 0.5h increment (keeps half-hours, no rounding down)
export const roundUpToNearestHalfHour = (hours: number): number => {
  return Math.ceil(hours * 2) / 2;
};


// Check if a date falls within a specific date range, comparing only the date part (not time)
export const isDateInRange = (date: Date, rangeStart: Date, rangeEnd: Date): boolean => {
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStartOnly = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate());
  const rangeEndOnly = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate());
  
  return dateOnly >= rangeStartOnly && dateOnly <= rangeEndOnly;
};
